import { Injectable } from '@angular/core';
import { isEmpty } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { UserProfileService } from 'src/app/common/service/user-profile.service';
import { UserRole } from 'src/app/management/model/role';
import { MarkerType } from 'src/app/map/model/marker-type';
import { GpsFrameShowErrorStrategy } from 'src/app/vehicles/model/gps-frame';
import { PositionAgeThresholds } from 'src/app/vehicles/model/position-status';
import { Punctuality } from '../model/punctuality';
import { ServerSetting } from '../model/server-setting';
import { Setting, SettingType } from '../model/setting';
import * as Settings from '../setting-const';
import { SETTING_VALUE_DISABLED, SETTINGS_GROUPS } from '../setting-const';
import {
  getSetting,
  getSettingArray,
  getSettingAsBoolean,
  getSettingAsNumber,
  getSettingsGroups,
  getSettingsOfSettingsGroup,
  SettingsMap
} from '../utils/settings-utils';
import { SettingsService } from './settings.service';


@Injectable({
  providedIn: 'root'
})
export class UserSettingsService {


  private settings: SettingsMap = {};
  private settingsLoaded: boolean = false;
  private vehiclePopupContent: string[];
  private busStopPopupContent: string[];

  constructor(private settingsService: SettingsService, private userProfileService: UserProfileService) {
  }

  loadSettings(): Observable<void> {
    const subject = new Subject<void>();
    this.settingsService.getUserSettings().subscribe(
      response => {
        response.data.forEach(setting => this.settings[setting.key] = setting.value);
        this.settingsLoaded = true;
        subject.next();
        subject.complete();
      },
      error => {
        console.error(error);
        // TODO handle error
        subject.error(error);
        subject.complete();
      });
    return subject;
  }

  isSettingsLoaded(): boolean {
    return this.settingsLoaded;
  }

  clearSettings(): void {
    this.settings = {};
    this.settingsLoaded = false;
  }

  private getSetting(key: string): string | null {
    return getSetting(key, this.settings, Settings.SETTINGS_MAP);
  }

  private getSettingArray(key: string): string[] | null {
    return getSettingArray(key, this.settings, Settings.SETTINGS_MAP);
  }

  getSettingsGroups(): string[] {
    return getSettingsGroups(SETTINGS_GROUPS);
  }

  getSettingsOfSettingsGroup(groupName: string): Setting[] {
    return getSettingsOfSettingsGroup(groupName, SETTINGS_GROUPS);
  }

  getSettingOf(serverSetting: ServerSetting): Setting {
    return Settings.SETTINGS_MAP[serverSetting.key]
      ? Settings.SETTINGS_MAP[serverSetting.key]
      : { name: serverSetting.key, type: SettingType.TEXT } as Setting;
  }

  private getSettingAsNumber(key: string): number | null {
    return getSettingAsNumber(key, this.settings, Settings.SETTINGS_MAP);
  }

  getSettingAsBoolean(key: string): boolean | null {
    return getSettingAsBoolean(key, this.settings, Settings.SETTINGS_MAP);
  }

  getShowBusStopsMenuItem(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_BUS_STOPS_MENUITEM);
  }

  shouldShowBusStopsReports(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_BUS_STOPS_REPORTS_MENUITEM);
  }

  shouldShowMzaReports(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_MZA_REPORTS_MENUITEM);
  }

  shouldShowDevicesStatuses(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_DEVICES_BUTTON);
  }

  getMapInitialLatitude(): number {
    return this.getSettingAsNumber(Settings.SETTING_MAP_INITIAL_LATITUDE);
  }

  getMapInitialLongitude(): number {
    return this.getSettingAsNumber(Settings.SETTING_MAP_INITIAL_LONGITUDE);
  }

  getMapInitialZoom(): number {
    return this.getSettingAsNumber(Settings.SETTING_MAP_INITIAL_ZOOM);
  }

  getShowPositionHistoryMenuItemMinRole(): string {
    return this.getSetting(Settings.SETTING_POSITION_HISTORY_ADMIN_MENU_ITEM_MIN_ROLE);
  }

  shouldShowPositionHistoryMenuItem(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getShowPositionHistoryMenuItemMinRole());
  }

  isZonesEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_ZONES_ENABLED);
  }

  isVehicleDeviceMapEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_DEVICE_MAP_ENABLED);
  }

  getShowZonesManagement(): string {
    return this.getSetting(Settings.SETTING_ZONES_MANAGEMENT_MIN_ROLE);
  }

  shouldShowZonesManagement(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getShowZonesManagement());
  }

  isVehiclePropertiesEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_PROPERTIES_ENABLED);
  }

  isVehicleClientsVisibilityEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_CLIENT_VISIBILITY_ENABLED);
  }

  shouldAllowVehiclePropertiesEditing(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getSetting(Settings.SETTING_VEHICLE_PROPERTIES_EDITING_MIN_ROLE));
  }

  shouldAllowEditResetStops(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getSetting(Settings.SETTING_RESET_STOPS_EDITING_MIN_ROLE));
  }

  shouldAllowVehicleGroupEditing(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_GROUPS_EDITING_ENABLED);
  }

  shouldAllowVehiclePropertiesValuesEditing(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getSetting(Settings.SETTING_VEHICLE_PROPERTIES_VALUES_EDITING_MIN_ROLE));
  }

  shouldAllowVehicleConfiguration(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_CONFIGURATION_ENABLED) &&
      this.userProfileService.isUserHasMinRole(this.getSetting(Settings.SETTING_VEHICLE_CONFIGURATION_MIN_ROLE));
  }

  shouldShowVehicleManagement(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getSetting(Settings.SETTING_VEHICLE_MANAGEMENT_MIN_ROLE));
  }

  shouldShowVehicleGroups(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getSetting(Settings.SETTING_VEHICLE_GROUPS_MIN_ROLE));
  }

  getVehicleMarker(): string {
    return this.getSetting(Settings.SETTING_VEHICLE_MARKER);
  }

  getPunctuality(): Punctuality {
    return {
      early: this.getSettingAsNumber(Settings.SETTING_PUNCTUALITY_EARLY),
      delayed: this.getSettingAsNumber(Settings.SETTING_PUNCTUALITY_DELAYED),
      late: this.getSettingAsNumber(Settings.SETTING_PUNCTUALITY_LATE)
    };
  }

  isLiveDataReportEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_LIVE_DATA_REPORT_ENABLED);
  }

  isPanicButtonCameraPreviewEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_PANIC_BUTTON_CAMERA_VIDEO_PREVIEW_ENABLED);
  }

  getVehiclePopupContent(): string[] {
    if (!this.vehiclePopupContent) {
      const stringValue = this.getSetting(Settings.SETTING_VEHICLE_POPUP_CONTENT);
      this.vehiclePopupContent = stringValue.split(',');
    }
    return this.vehiclePopupContent;
  }

  getBusStopMarker(): MarkerType {
    const setting = this.getSetting(Settings.SETTING_BUS_STOP_MARKER);
    return Settings.MARKERS_SETTINGS[setting];
  }

  getBusStopPopupContent(): string[] {
    if (!this.busStopPopupContent) {
      const stringValue = this.getSetting(Settings.SETTING_BUS_STOP_POPUP_CONTENT);
      this.busStopPopupContent = stringValue.split(',');
    }
    return this.busStopPopupContent;
  }

  getBusStopReportMaxDaysRange(): number {
    return this.getSettingAsNumber(Settings.SETTING_MAX_DAYS_REPORT_REQUEST);
  }

  isBusStopReportChartExportEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_BUS_STOPS_REPORTS_CHARTS_ENABLED);
  }

  isCarriersHandlingEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_CARRIERS_HANDLING_ENABLED);
  }

  shouldOpenFiltersOnVehiclesPage(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_OPEN_FILTERS_ON_VEHICLES_PAGE);
  }

  showPolylineForHistoryGPSFrames(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SHOW_POLYLINE_FOR_HISTORY_GPS_FRAMES);
  }

  getDirectionRouteSource(): string {
    return this.getSetting(Settings.SETTING_DIRECTION_ROUTE_SOURCE);
  }

  showGroupsToAdmins(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SHOW_GROUPS_TO_ADMINS);
  }

  isUserManagementEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_USER_MANAGEMENT_ENABLED);
  }

  getMessagesMenuItem(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_INFO_MESSAGES_ENABLED);
  }

  isShowOnlyOnCourseEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SHOW_ONLY_ON_COURSE_ENABLED);
  }

  isVehicleCameraPreviewEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_CAMERA_PREVIEW_ENABLED);
  }

  isMakeSnapshotsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_MAKE_SNAPSHOTS_ENABLED);
  }

  isDeleteDriverSnapshotsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_DELETE_DRIVER_SNAPSHOTS_ENABLED);
  }

  isNotifyOnNewSnapshotFileEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_NOTIFY_ON_NEW_SNAPSHOT_FILE);
  }

  getMinutesToMarkVehicleAsOldPosition(): number | null {
    return this.getSettingAsNumber(Settings.SETTING_VEHICLE_OLD_POSITION_AFTER_MINUTES);
  }

  getMinutesToMarkVehiclePositionAsExpired(): number | null {
    return this.getSettingAsNumber(Settings.SETTING_VEHICLE_EXPIRED_POSITION_AFTER_MINUTES);
  }

  // Oba progi pobierać razem - odbiorcy liczą z nich jeden stan i osobno mogłyby się rozjechać.
  getPositionAgeThresholds(): PositionAgeThresholds {
    return {
      staleAfterMinutes: this.getMinutesToMarkVehicleAsOldPosition(),
      expiredAfterMinutes: this.getMinutesToMarkVehiclePositionAsExpired()
    };
  }

  getShowLinePrioritiesMinRole(): string {
    return this.getSetting(Settings.SETTING_SHOW_LINE_PRIORITIES);
  }

  shouldShowLinePriorities(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getShowLinePrioritiesMinRole());
  }

  isAdminSettingsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_ADMIN_SETTINGS_ENABLED);
  }

  getVehicleDistanceReportMaxDaysRange(): number {
    return this.getSettingAsNumber(Settings.SETTING_MAX_DAYS_VEHICLE_DISTANCE_REPORT_REQUEST);
  }

  isVehicleDistanceReportEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_DISTANCE_WINDOW_ENABLED);
  }

  isUserActivityLogEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_USER_ACTIVITY_LOG_ENABLED);
  }

  isUserActivityLogVisible(): boolean {
    const minRole = this.getUserActivityLogMinRole();
    return this.userProfileService.isService() || this.userProfileService.isUserHasMinRole(minRole);
  }

  getUserActivityLogMinRole(): string {
    return this.getSetting(Settings.SETTINGS_USER_ACTIVITY_LOG_MIN_ROLE);
  }

  canAccessUserActivityLog(): boolean {
    return this.isUserActivityLogEnabled() && this.isUserActivityLogVisible();
  }

  isSegmentTravelTimeEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SEGMENT_TRAVEL_TIME_WINDOW_ENABLED);
  }

  getSegmentTravelTimeReportMaxDaysRange(): number {
    return this.getSettingAsNumber(Settings.SETTING_MAX_DAYS_SEGMENT_TRAVEL_TIME_REPORT_REQUEST);
  }

  showVehicleTeamAssignments(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_TEAM_ASSIGNMENT_ENABLED);
  }

  isAdsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_ADS_ENABLED);
  }

  getAdsExternalReportsSystemUrl(): string | null {
    return this.getSetting(Settings.SETTING_ADS_EXTERNAL_REPORTS_SYSTEM_URL);
  }

  isArchiveEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_ARCHIVE_ENABLED);
  }

  isPanicButtonReportEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_PANIC_BUTTON_REPORT_ENABLED);
  }

  getUserActivityTypes(): string[] {
    return this.getSettingArray(Settings.SETTING_USER_ACTIVITY_TYPES);
  }

  getUserFileSourceTypes(): string[] {
    return this.getSettingArray(Settings.SETTING_USER_FILE_SOURCE_TYPES);
  }

  shouldShowFilesToDownload(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SHOW_FILES_TO_DOWNLOAD);
  }

  shouldShowLiveVehiclesMap(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SHOW_LIVE_VEHICLES_MAP);
  }

  isVehicleStatusesSyncEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_SYNC_STATUSES_ENABLED);
  }

  getVehicleStatusesSyncMaxDaysRange(): number {
    return this.getSettingAsNumber(Settings.SETTING_VEHICLE_SYNC_STATUSES_MAX_DAYS);
  }

  isPassengerReportsCorrectionEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_PASSENGER_REPORTS_CORRECTION_ENABLED);
  }

  getRouteIdentifier(): string {
    return this.getSetting(Settings.SETTING_ROUTE_IDENTIFIER_FIELD);
  }

  getBusStopReportNameOptionSetting(): string {
    return this.getSetting(Settings.SETTING_BUS_STOP_REPORT_NAME_OPTION);
  }

  getMzaReportNameOptionSetting(): string {
    return this.getSetting(Settings.SETTING_MZA_REPORTS_NAME);
  }

  isDatasyncEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_DATASYNC_ENABLED);
  }

  getVehicleBusStopMissedColor(): string {
    return this.getSetting(Settings.SETTING_COLORING_VEHICLE_BUS_STOP_MISSED);
  }

  iSVehicleLayoutEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_LAYOUT_ENABLED);
  }

  isGraphReportEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_GRAPH_REPORT_ENABLED);
  }

  isShowPhantomsEnable(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SHOW_PHANTOMS_ENABLED);
  }

  getVehicleLayoutConfigurationMinRole(): string {
    return this.getSetting(Settings.SETTINGS_VEHICLES_LAYOUT_CONFIGURATION_MIN_ROLE);
  }

  shouldAllowVehiclesLayoutConfiguration(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getVehicleLayoutConfigurationMinRole());
  }

  getErrorManagementMinRole(): string {
    return this.getSetting(Settings.SETTINGS_ERROR_MANAGMENT_MIN_ROLE);
  }

  shouldAllowErrorManagement(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getErrorManagementMinRole());
  }

  isImportStatisticsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_IMPORT_STATISTICS_ENABLED);
  }

  isVehicleTypeEditEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_TYPE_EDIT_ENABLED);
  }

  isSnvrPlayerAllowed(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SNVR_PLAYER_ENABLED) && this.userProfileService.isMPK();
  }

  snvrEnabledModules(): Array<string> {
    return this.getSettingArray(Settings.ENABLED_SNVR_MODULES);
  }

  snvrMaxRequestTimeRange(): number {
    return this.getSettingAsNumber(Settings.SETTING_SNVR_MAX_REQUEST_TIME_RANGE);
  }

  getSnvrServerEditMinRole(): string {
    return this.getSetting(Settings.SETTING_SNVR_EDIT_MODE_MIN_ROLE);
  }

  shouldShowSnvrServerEdit(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getSnvrServerEditMinRole());
  }

  shouldShowStopPileStatDataReport(): boolean {
    return (this.stopPileStatDataReportMinRole() && this.getSettingAsBoolean(Settings.SETTING_STOPPILE_STAT_DATA_REPORT_ENABLED))
      || this.userProfileService.isService();
  }

  stopPileStatDataReportMinRole(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getSetting(Settings.SETTING_STOPPILE_STAT_DATA_REPORT_MIN_ROLE));
  }

  isPassengerResetStopsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_PASSENGER_RESET_STOPS_ENABLED);
  }

  isErrorLogReportEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_ERROR_LOG_REPORT_ENABLE);
  }

  getDiagnosicModuleSelectedDevices(): string[] {
    return this.getSettingArray(Settings.SETTING_ERROR_LOG_REPORT_DEVICES);
  }

  isTmlMessagesEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_TML_ENABLED);
  }

  isAdsAlarmNotificationsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_ADS_ALARM_NOTIFICATIONS_ENABLED);
  }

  adsPreviewEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTINGS_ADS_PREVIEW_ENABLED);
  }

  getAvailableDeviceSettings(): string[] {
    return this.getSettingArray(Settings.SETTING_AVAILABLE_DEVICE_SETTINGS);
  }

  getAdsAdvertisementOptions(): string[] {
    return this.getSettingArray(Settings.SETTINGS_ADS_ADVERTISEMENT_OPTIONS);
  }

  getAdsAlarmOptions(): string[] {
    return this.getSettingArray(Settings.SETTINGS_ADS_ALARM_OPTIONS);
  }

  isVehicleIconMenuEnabled(): boolean {
    return this.vehicleDialogsEnabled().includes(Settings.SETTINGS_OPEN_CAMERA_CONFIG_DIALOG_ENABLED);
  }

  vehicleDialogsEnabled(): Array<string> {
    return this.getSettingArray(Settings.SETTINGS_DIALOGS_IN_VEHICLE_MANAGEMENT);
  }

  isAutomaticalyAddSnvrInfrastuctureEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTINGS_SNVR_AUTOMATICALY_ADD_INFRASTUCTURE_ENABLED);
  }

  cameraSettingsRedirectionEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.CAMERA_SETTINGS_REDIRECTION_ENABLED);
  }

  isRemedialStepsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTINGS_REMEDIAL_STEPS_ENABLED);
  }

  shouldShowDepots(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_DEPOTS_ENABLED);
  }

  shouldAllowDepotsManagement(): boolean {
    const role = this.getSetting(Settings.ALLOW_DEPOTS_MANAGEMENT);
    return this.userProfileService.isUserHasMinRole(role);
  }

  isEnableDeviceSoftwareUpdate(): boolean {
    return this.vehicleDialogsEnabled().includes(Settings.SETTING_ENABLE_DEVICE_SOFTWARE_UPDATE);
  }

  getMinUserRoleToDoDeviceSoftwareUpdate(): string {
    return this.getSetting(Settings.SETTING_MIN_USER_ROLE_TO_DO_DEVICE_SOFTWARE_UPDATE);
  }

  isSoftwareCampaignEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SOFTWARE_CAMPAIGN_ENABLED);
  }

  softwareCampaignAvailableDevices(): Array<string> {
    return this.getSettingArray(Settings.SETTING_SOFTWARE_CAMPAIGN_AVAILABLE_DEVICES);
  }

  getAvailableDevicesForGlobalDeviceSettings(): string[] {
    return this.getSettingArray(Settings.SETTINGS_GLOBAL_PANEL_AVAILABLE_DEVICES) ?? [];
  }

  getAvailableDevicesForSettingsPanel(): string[] {
    return this.getSettingArray(Settings.SETTINGS_PANEL_AVAILABLE_DEVICES) ?? [];
  }

  isEnableDoingUpdatesWhileUpdatePending(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SOFTWARE_CAMPAIGN_DISABLE_DOING_UPDATES_WHILE_UPDATE_PENDING);
  }

  allowedImportsList(): Array<string> {
    return this.getSettingArray(Settings.SETTINGS_ALLOWED_IMPORTS_LIST);
  }

  isAnyImportAllowed(): boolean {
    return this.allowedImportsList().length > 0;
  }

  isScheduleEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SCHEDULE_ENABLED);
  }

  isVoiceChatEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VOICE_CHAT_ENABLED);
  }

  shouldShowPlaylistFileReport(): boolean {
    return this.isAdsEnabled() && this.getSettingAsBoolean(Settings.SETTING_SHOULD_SHOW_PLAYLIST_FILE_REPORT);
  }

  isFileStatusHandleUpdateEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_HANDLE_FILE_STATUS_UPDATE_ENABLED) && this.userProfileService.isService();
  }

  isChatEnabled(): any {
    return this.getSettingAsBoolean(Settings.SETTING_CHAT_ENABLED);
  }

  isVehicleDisplaySyncEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_VEHICLE_DISPLAY_SYNC_ENABLED);
  }

  isReverseTimetableDeviationEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_REVERCE_TIMETABLE_DEVIATION);
  }

  isEventOccurenceTimeEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_EVENT_OCCURRENCE_TIME_ENABLED);
  }

  isDownloadZippedPlaylistReportEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_DOWNLOAD_ZIPPED_PLAYLIST_REPORT_ENABLED);
  }

  isShowRoadSchedule(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_ROAD_SCHEDULE_IN_MENU_ENABLED);
  }

  getAdsAcceptableExtensions(): string {
    const extensions = this.getSetting(Settings.SETTING_ADS_ACCEPTABLE_EXPENSIONS_ENABLED);
    if (!isEmpty(extensions)) {
      const trimed = extensions.replace(/\s/g, '');
      const toReturn = trimed.split(',').join(', ');
      return toReturn;
    } else
      return '';
  }

  getAdsAcceptableExtensionsWithDots(): string {
    const extensions = this.getSetting(Settings.SETTING_ADS_ACCEPTABLE_EXPENSIONS_ENABLED);
    if (!isEmpty(extensions)) {
      const trimed = extensions.replace(/\s/g, '');
      const toReturn = trimed.split(',').map(x => '.' + x).join(', ');
      return toReturn;
    } else
      return '';
  }

  shouldShowSilDataUploadReport(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SIL_DATA_UPLOAD_REPORT_ENABLED);
  }

  getTranslationsManagementMinRole(): string {
    return this.getSetting(Settings.TRANSLATIONS_MANAGEMENT_MIN_ROLE);
  }

  isTranslationsManagementEnabled(): boolean {
    return this.userProfileService.isUserHasMinRole(this.getTranslationsManagementMinRole());
  }


  getVisibleUserRoles(): UserRole[] {
    return this.getSettingArray(Settings.VISIBLE_USER_ROLES) as UserRole[];
  }

  isPassengerReportEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_PASSENGER_REPORT_ENABLED);
  }

  isShowLoggedUsersMapMenuItemEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_SHOW_USER_LOGGED_MENU_ITEM);
  }

  getGpsFrameShowErrorStrategy(): GpsFrameShowErrorStrategy {
    return this.getSetting(Settings.SETTING_SHOW_MAP_VEHICLE_ERROR_STRATEGY) as GpsFrameShowErrorStrategy;
  }

  isAlcoholTestRequestEnable(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_ALCOHOL_TEST_REQUEST);
  }

  isSakpEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SAKP_ENABLED);
  }

  isSakpColorizeRowsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SAKP_COLORIZE_ROWS_ENABLED);
  }

  getSakpDepartureReportVisibleFieldsNames(): Array<string> {
    return this.getSettingArray(Settings.SAKP_DEPARTURE_REPORT_FIELDS);
  }

  isDiagnosticsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTINGS_DIAGNOSTICS_ENABLED);
  }

  isDiagnosticsGrafanaEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTINGS_DIAGNOSTICS_GRAFANA_ENABLED);
  }

  isVoiceAnnouncementsEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTINGS_VOICE_ANNOUCEMENTS_ENABLED);
  }

  shouldAllowEditingWhenFleetmanagementDeadlineClosed(): boolean {
    return this.getSettingAsBoolean(Settings.SAKP_FLEETMANAGEMENT_EDITING_AFTER_DEADLINE_ENABLED);
  }

  getFleetmanagementNumberOfEditableDaysAfterDeadline(): number {
    return this.getSettingAsNumber(Settings.SAKP_FLEETMANAGEMENT_NUMBER_OF_EDITABLE_DAYS_AFTER_DEADLINE);
  }

  getDevicesMinRole(): string {
    return this.getSetting(Settings.SETTING_DEVICES_MIN_ROLE);
  }

  getShowUserLoggedNoInternetStatusInMinutes(): number {
    return this.getSettingAsNumber(Settings.SETTING_SHOW_USER_LOGGED_NO_INTERNET_TIMEOUT);
  }

  isAccumulatedDataReportEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.SETTING_ACCUMULATED_DATA_REPORT_ENABLED);
  }

  isUserRoleForAccumulatedDataReportGood(): boolean {
    const role = this.getSetting(Settings.SETTING_ACCUMULATED_DATA_REPORT_MIN_ROLE);
    return this.userProfileService.isUserHasMinRole(role);
  }

  isUserRoleForBusStopReportGood(): boolean {
    const role = this.getSetting(Settings.SETTING_BUS_STOPS_STATISTICS_MIN_ROLE);
    return this.userProfileService.isUserHasMinRole(role);
  }

  isTablePrintEnabled(): boolean {
    return this.getSettingAsBoolean(Settings.ENABLE_TABLE_PRINT);
  }

  canAccessDevicesStatus(): boolean {
    const minRole = this.getDevicesMinRole();

    if (minRole !== SETTING_VALUE_DISABLED) {
      return this.userProfileService.isUserHasMinRole(minRole);
    }

    return false;
  }

  getFilterHistoryMaxSize(): number {
    return this.getSettingAsNumber(Settings.FILTER_HISTORY_MAX_SIZE);
  }

  /**
   * Wspolne zrodlo prawdy dla kafelka "raporty" na home, guarda huba /reports
   * i widocznosci pozycji w samym hubie - musi obejmowac wszystkie pozycje z ReportsComponent.
   */
  canAccessAnyReport(): boolean {
    return this.shouldShowBusStopsReports()
      || this.shouldShowMzaReports()
      || this.canAccessDevicesStatus()
      || this.shouldShowPositionHistoryMenuItem()
      || this.isVehicleDistanceReportEnabled()
      || this.isSegmentTravelTimeEnabled()
      || this.isPanicButtonReportEnabled()
      || this.shouldShowStopPileStatDataReport()
      || this.isErrorLogReportEnabled()
      || this.shouldShowPlaylistFileReport()
      || this.shouldShowSilDataUploadReport()
      || this.isDownloadZippedPlaylistReportEnabled()
      || this.canAccessAccumulatedDataReport();
  }

  canAccessAccumulatedDataReport(): boolean {
    return this.isAccumulatedDataReportEnabled() && this.isUserRoleForAccumulatedDataReportGood();
  }

}
